import { useState, type ReactNode } from "react";
import { Container } from "./ui";
import { useLocale } from "../i18n/useLocale";
import { TIME_PLANS, PROJECT_PLANS, type Plan, localizedPlans, countryFor } from "../content/pricing";
import { PricingPlanCard, PricingToggle, useIsDesktop } from "./PricingTable";

/**
 * PricingPlans — the home pricing section. Each family is self-contained: its
 * title sits above its own month/year switcher, then the cards. Two independent
 * switchers (Time / Project). Responsive cards: mobile stacked, tablet a
 * horizontal swipe row, desktop a grid (Time fans in). Data from content/pricing.
 */

function cn(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

// Cards stay stacked on mobile AND tablet (the card itself goes horizontal on
// tablet); only the desktop spreads them into a grid.
const CARDS_BASE = "flex flex-col gap-6 lg:grid lg:gap-6";

function PricingFamily({ id, eyebrow, title, plans, fan, cols, currency = "eur" }: {
  id: string; eyebrow: string; title: string; plans: Plan[]; fan: boolean; cols: string; currency?: string;
}) {
  const [annual, setAnnual] = useState(false);
  const isDesktop = useIsDesktop();

  return (
    <div>
      {/* Family title + switcher — centred, above the cards */}
      <div className="mb-6 text-center">
        <p className="text-ds-14 font-semibold uppercase tracking-[0.14em] text-ink-faint">{eyebrow}</p>
        <h3 className="mt-1 font-display text-ds-26 font-semibold tracking-tight text-ds-ink">{title}</h3>
      </div>

      <PricingToggle id={id} annual={annual} setAnnual={setAnnual} className="mb-14 justify-center" />

      <div className={cn(CARDS_BASE, cols)}>
        {plans.map((plan, i) => (
          <PricingPlanCard key={plan.name} plan={plan} annual={annual} index={i} isDesktop={isDesktop} fan={fan} currency={currency} />
        ))}
      </div>
    </div>
  );
}

export default function PricingPlans({ eyebrow, title, subtitle }: { eyebrow?: ReactNode; title?: ReactNode; subtitle?: ReactNode }) {
  const { locale, market, t } = useLocale();
  const hasHeading = Boolean(eyebrow || title || subtitle);
  // Per-market currency, baked from the catalog at build (S9); EUR fallback.
  const country = countryFor(locale);
  const time = localizedPlans(TIME_PLANS, country);
  const project = localizedPlans(PROJECT_PLANS, country);

  return (
    <Container className="py-16 font-ds md:py-20">
      {/* Section title (optional) */}
      {hasHeading && (
        <div className="mx-auto max-w-2xl text-center">
          {eyebrow}
          {title && <h2 className="mt-3 text-ds-30 font-semibold leading-[1.12] tracking-[-0.5px] text-ds-ink lg:text-ds-34">{title}</h2>}
          {subtitle && <p className="mt-4 text-ds-18 leading-relaxed text-ds-ink-soft">{subtitle}</p>}
        </div>
      )}

      {/* Time & operations */}
      <div className={hasHeading ? "mt-14" : ""}>
        <PricingFamily id="time" eyebrow={t("pricing:family.time.eyebrow")} title={t("pricing:family.time.title")} plans={time.plans} currency={time.currency} fan cols="lg:grid-cols-3" />
      </div>

      {/* Project & cost control */}
      <div className="mt-[90px]">
        <PricingFamily id="project" eyebrow={t("pricing:family.project.eyebrow")} title={t("pricing:family.project.title")} plans={project.plans} currency={project.currency} fan={false} cols="lg:grid-cols-2 lg:mx-auto lg:max-w-[760px]" />
      </div>

      {/* Why Project costs more — plain white card under the project tariffs */}
      <div className="mt-8 rounded-3xl border border-line bg-surface p-4 md:p-8 shadow-[0_1px_3px_rgba(15,23,42,0.05)] lg:mx-auto lg:max-w-[760px]">
        <p className="text-ds-14 leading-relaxed text-ds-ink-soft">
          {t("pricing:whyMore.lead")} <span className="font-semibold text-ds-ink">{t("pricing:whyMore.emphasis")}</span>{market === "belgium" ? t("pricing:whyMore.body.be") : t("pricing:whyMore.body.intl")}
        </p>
      </div>
    </Container>
  );
}
